import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mountain, Copy, Check, ExternalLink, MessageCircle, Search, Lock, Pencil, X, Save, Instagram, Send, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const playfair = { fontFamily: "'Playfair Display', Georgia, serif" };

const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const PWD_KEY = 'gohi_directory_admin_pwd_v1';

// Visual tier labels. Note: "discovered" is a derived bucket — scraped stays
// that have a WhatsApp number get this label so a EnquireModal booking flow
// is enabled on the public page. Raw scraped stays without WhatsApp remain
// "Scraped".
const TIER_BADGE = {
  featured:    { label: 'Featured',    cls: 'bg-amber-100 text-amber-900 border-amber-200' },
  partner:     { label: 'Collective',  cls: 'bg-emerald-100 text-emerald-900 border-emerald-200' },
  discovered:  { label: 'Discovered',  cls: 'bg-sky-100 text-sky-900 border-sky-200' },
  scraped:     { label: 'Scraped',     cls: 'bg-stone-100 text-stone-700 border-stone-200' },
};

// Compute the *effective* tier from raw fields: any scraped stay with a
// WhatsApp number becomes "discovered" (booking-enabled).
const effectiveTier = (s) => {
  const ps = (s.partnership_status || '').toLowerCase();
  if (ps === 'featured') return 'featured';
  if (ps === 'partner' || ps === 'valley_partner') return 'partner';
  if (ps === 'scraped' && (s.whatsapp_number || '').trim()) return 'discovered';
  return 'scraped';
};

/**
 * /directory — internal founder view of every active property + the master
 * list to capture host name + WhatsApp. Auto-numbered (1, 2, 3…).
 *
 * Edits require the admin password (cached in localStorage across the session).
 */
export default function FoundersLinks() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [query, setQuery] = useState('');
  const [tier, setTier] = useState('all');
  const [copied, setCopied] = useState('');

  // Edit mode + password
  const [adminPwd, setAdminPwd] = useState(() => {
    try { return localStorage.getItem(PWD_KEY) || ''; } catch (e) { return ''; }
  });
  const [showPwdPrompt, setShowPwdPrompt] = useState(false);
  const [pwdInput, setPwdInput] = useState('');
  const [editingId, setEditingId] = useState(null);    // gohimalayan_id being edited
  const [editForm, setEditForm] = useState({ host_name: '', whatsapp_number: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Outreach quick-action state — per-row contacted flag (optimistic UI)
  // and any "in-flight" gohimalayan_id we're updating right now.
  const [contactingId, setContactingId] = useState(null);
  const [contactError, setContactError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/api/founders/links`);
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed to load');
      setData(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key); setTimeout(() => setCopied(''), 1500);
    }).catch(() => {});
  };

  // ---------- Edit handlers ----------
  const startEdit = (s) => {
    if (!adminPwd) { setShowPwdPrompt(true); return; }
    setEditingId(s.gohimalayan_id);
    setEditForm({
      host_name: s.host_name || '',
      whatsapp_number: s.whatsapp_number || '',
    });
    setSaveError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ host_name: '', whatsapp_number: '' });
    setSaveError('');
  };

  const saveEdit = async (s) => {
    setSaving(true); setSaveError('');
    const params = new URLSearchParams();
    params.set('password', adminPwd);
    // Identify by display_name (unique across active stays in our data)
    params.set('display_name', s.display_name);
    params.set('host_name', editForm.host_name.trim());
    params.set('whatsapp_number', editForm.whatsapp_number.trim());
    try {
      const r = await fetch(`${API}/api/admin/curation/set-stay-meta?${params.toString()}`, { method: 'POST' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Save failed');
      // Optimistic patch + full reload to be safe
      cancelEdit();
      await load();
    } catch (e) {
      setSaveError(e.message);
      // If auth failed, clear the cached password so the user can re-enter
      if ((e.message || '').toLowerCase().includes('invalid') || (e.message || '').toLowerCase().includes('password')) {
        try { localStorage.removeItem(PWD_KEY); } catch (_) {}
        setAdminPwd('');
      }
    } finally {
      setSaving(false);
    }
  };

  // Push a Discovered stay into the outreach pipeline as "dm_sent". Idempotent:
  // /api/outreach/add is upsert-safe, /update flips the status. Re-loads the
  // table so the persisted pill renders without a manual refresh.
  const markContacted = async (s) => {
    if (!adminPwd) { setShowPwdPrompt(true); return; }
    if (!s.ig_handle) { setContactError('No Instagram handle on this stay.'); return; }
    setContactingId(s.gohimalayan_id);
    setContactError('');
    try {
      const meta = {
        handle: s.ig_handle,
        property_name: s.display_name || '',
        location: [s.valley, s.state].filter(Boolean).join(', '),
      };
      // 1. Upsert target (safe to call repeatedly)
      const addR = await fetch(`${API}/api/outreach/add?password=${encodeURIComponent(adminPwd)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(meta),
      });
      if (!addR.ok) {
        const j = await addR.json().catch(() => ({}));
        throw new Error(j.detail || 'Could not add target');
      }
      // 2. Move it to dm_sent + capture WhatsApp if we have one
      const upR = await fetch(`${API}/api/outreach/update?password=${encodeURIComponent(adminPwd)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handle: s.ig_handle,
          status: 'dm_sent',
          property_name: meta.property_name,
          location: meta.location,
          whatsapp: s.whatsapp_number || null,
        }),
      });
      if (!upR.ok) {
        const j = await upR.json().catch(() => ({}));
        throw new Error(j.detail || 'Could not update status');
      }
      await load();
    } catch (e) {
      setContactError(e.message);
      if ((e.message || '').toLowerCase().includes('invalid') || (e.message || '').toLowerCase().includes('password')) {
        try { localStorage.removeItem(PWD_KEY); } catch (_) {}
        setAdminPwd('');
      }
    } finally {
      setContactingId(null);
    }
  };

  const submitPwd = () => {
    const pwd = pwdInput.trim();
    if (!pwd) return;
    try { localStorage.setItem(PWD_KEY, pwd); } catch (_) {}
    setAdminPwd(pwd);
    setShowPwdPrompt(false);
    setPwdInput('');
  };

  // ---------- Filter ----------
  const allStays = data?.stays || [];
  const discoveredCount = allStays.filter((s) => effectiveTier(s) === 'discovered').length;
  const filtered = allStays.filter((s) => {
    if (tier !== 'all' && effectiveTier(s) !== tier) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (s.display_name || '').toLowerCase().includes(q) ||
      (s.valley || '').toLowerCase().includes(q) ||
      (s.host_name || '').toLowerCase().includes(q) ||
      (s.state || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Mountain size={20} className="text-stone-900" strokeWidth={1.5} />
            <div>
              <p className="text-[10px] tracking-[0.3em] uppercase text-amber-700 font-semibold">Founders</p>
              <h1 className="text-lg text-stone-900 leading-none" style={playfair}>Master Directory</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 text-xs text-stone-600">
            <span><b className="text-stone-900">{data?.count || 0}</b> active</span>
            <span><b className="text-amber-700">{data?.by_tier?.featured || 0}</b> featured</span>
            <span><b className="text-emerald-700">{data?.by_tier?.collective || 0}</b> collective</span>
            <span><b className="text-sky-700">{discoveredCount}</b> discovered</span>
            <span><b className="text-stone-700">{(data?.by_tier?.scraped || 0) - discoveredCount}</b> scraped</span>
            <button onClick={() => load()} className="text-stone-500 underline hover:text-stone-900">refresh</button>
            {adminPwd ? (
              <button
                onClick={() => { try { localStorage.removeItem(PWD_KEY); } catch (_) {} setAdminPwd(''); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 text-[11px]"
                data-testid="directory-lock-btn"
              ><Lock size={11} /> Edit unlocked · lock</button>
            ) : (
              <button
                onClick={() => setShowPwdPrompt(true)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-stone-900 text-white hover:bg-stone-800 text-[11px]"
                data-testid="directory-unlock-btn"
              ><Lock size={11} /> Unlock to edit</button>
            )}
          </div>
        </div>
        {/* Search + tier filter */}
        <div className="max-w-7xl mx-auto px-6 pb-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" strokeWidth={1.5} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, valley, host, state…"
              className="w-full pl-9 pr-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-stone-900"
              data-testid="founders-search"
            />
          </div>
          <div className="flex gap-1">
            {['all', 'featured', 'partner', 'discovered', 'scraped'].map((t) => (
              <button
                key={t}
                onClick={() => setTier(t)}
                className={`text-[11px] px-3 py-1.5 rounded-full transition ${tier === t ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-700 hover:border-stone-400'}`}
                data-testid={`founders-tab-${t}`}
              >
                {t === 'partner' ? 'Collective' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Password prompt modal */}
      {showPwdPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4" data-testid="directory-pwd-modal">
          <div className="bg-white rounded-xl border border-stone-200 p-6 max-w-sm w-full">
            <div className="flex items-center gap-2 mb-3">
              <Lock size={16} className="text-amber-700" />
              <h2 className="text-base font-medium text-stone-900">Unlock edit mode</h2>
            </div>
            <p className="text-xs text-stone-500 mb-4">Enter the admin password to edit host names and WhatsApp numbers. Password is cached locally for this session.</p>
            <input
              type="password"
              value={pwdInput}
              onChange={(e) => setPwdInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submitPwd()}
              placeholder="Admin password"
              className="w-full px-3 py-2 border border-stone-300 rounded text-sm mb-3"
              data-testid="directory-pwd-input"
              autoFocus
            />
            <div className="flex gap-2">
              <button onClick={() => setShowPwdPrompt(false)} className="flex-1 px-3 py-2 text-sm bg-stone-100 hover:bg-stone-200 rounded">Cancel</button>
              <button onClick={submitPwd} className="flex-1 px-3 py-2 text-sm bg-stone-900 text-white hover:bg-stone-800 rounded" data-testid="directory-pwd-submit">Unlock</button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-6 py-6">
        <p className="text-[11px] text-stone-500 mb-3" data-testid="directory-count">
          Showing {filtered.length} of {allStays.length} · auto-updates as new stays go live
          {adminPwd && <span className="ml-2 text-emerald-700">· edit mode ON</span>}
        </p>

        {loading && <p className="text-sm text-stone-500">Loading…</p>}
        {error && <p className="text-sm text-rose-700">{error}</p>}
        {contactError && <p className="text-sm text-rose-700" data-testid="contact-error">{contactError}</p>}

        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid="founders-table">
          <div className="grid grid-cols-[40px_44px_1fr_140px_200px_88px_200px] gap-3 px-4 py-2.5 bg-stone-50 border-b border-stone-200 text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">
            <span>#</span>
            <span></span>
            <span>Property</span>
            <span>Valley · State</span>
            <span>Host</span>
            <span className="text-right">Traction</span>
            <span className="text-right">Actions</span>
          </div>

          {filtered.length === 0 && !loading && (
            <div className="px-6 py-12 text-center text-sm text-stone-500">No stays match your filter.</div>
          )}

          {filtered.map((s, idx) => {
            const tierMeta = TIER_BADGE[effectiveTier(s)] || TIER_BADGE.scraped;
            const publicUrl = `${ORIGIN}${s.public_path}`;
            const hostWa = (s.whatsapp_number || '').replace(/\D/g, '');
            const hostWaUrl = hostWa ? `https://wa.me/${hostWa}` : null;
            const isEditing = editingId === s.gohimalayan_id;

            return (
              <div
                key={s.gohimalayan_id || s.display_name}
                className={`grid grid-cols-[40px_44px_1fr_140px_200px_88px_200px] gap-3 px-4 py-3 border-b border-stone-100 last:border-b-0 items-center transition ${isEditing ? 'bg-amber-50' : 'hover:bg-amber-50/30'}`}
                data-testid={`founder-row-${s.gohimalayan_id}`}
              >
                {/* # ordinal */}
                <span className="text-xs text-stone-400 font-mono tabular-nums" data-testid={`founder-ordinal-${s.gohimalayan_id}`}>{idx + 1}</span>

                {/* thumb */}
                <div className="w-10 h-10 rounded-md overflow-hidden bg-stone-100 flex-shrink-0">
                  {s.thumb && <img src={s.thumb.startsWith('http') ? s.thumb : `${API}${s.thumb}`} alt="" className="w-full h-full object-cover" />}
                </div>

                {/* property */}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-stone-900 truncate">{s.display_name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded border ${tierMeta.cls}`}>{tierMeta.label}</span>
                    {s.starting_price_inr && <span className="text-[10px] text-stone-500">₹{s.starting_price_inr.toLocaleString('en-IN')}/n</span>}
                    {s.property_type && <span className="text-[10px] text-stone-400 truncate">· {s.property_type}</span>}
                  </div>
                </div>

                {/* valley + state */}
                <div className="text-xs text-stone-700 truncate">
                  <p className="truncate">{s.valley || <span className="text-stone-400 italic">unmapped</span>}</p>
                  <p className="text-[10px] text-stone-500 truncate">{s.state || '—'}</p>
                </div>

                {/* host — inline-editable */}
                <div className="text-xs min-w-0">
                  {isEditing ? (
                    <div className="space-y-1.5">
                      <input
                        value={editForm.host_name}
                        onChange={(e) => setEditForm({ ...editForm, host_name: e.target.value })}
                        placeholder="Host name"
                        className="w-full px-2 py-1 text-xs border border-stone-300 rounded focus:outline-none focus:border-stone-900"
                        data-testid={`edit-host-name-${s.gohimalayan_id}`}
                      />
                      <input
                        value={editForm.whatsapp_number}
                        onChange={(e) => setEditForm({ ...editForm, whatsapp_number: e.target.value })}
                        placeholder="+91 / +977 / +975 …"
                        className="w-full px-2 py-1 text-xs border border-stone-300 rounded focus:outline-none focus:border-stone-900 font-mono"
                        data-testid={`edit-host-wa-${s.gohimalayan_id}`}
                      />
                      {saveError && <p className="text-[10px] text-rose-700">{saveError}</p>}
                    </div>
                  ) : (
                    <>
                      <p className="text-stone-900 truncate">{s.host_name || <span className="text-stone-400 italic">not captured</span>}</p>
                      {hostWa ? (
                        <a href={hostWaUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-emerald-700 font-mono hover:underline" data-testid={`host-wa-${s.gohimalayan_id}`}>
                          +{hostWa}
                        </a>
                      ) : (
                        <span className="text-[10px] text-stone-400 italic">no WhatsApp</span>
                      )}
                    </>
                  )}
                </div>

                {/* traction */}
                <div className="text-right text-[10px] text-stone-600">
                  {s.enquiry_count > 0 && <p className="text-emerald-700 font-medium">⚡ {s.enquiry_count} enq</p>}
                  {s.lead_count > 0 && <p>{s.lead_count} click{s.lead_count === 1 ? '' : 's'}</p>}
                  {s.coupon_count > 0 && <p className="text-amber-700">🎁 {s.coupon_count} coup</p>}
                  {!s.enquiry_count && !s.lead_count && !s.coupon_count && <span className="text-stone-300">—</span>}
                </div>

                {/* actions */}
                <div className="flex justify-end gap-1 flex-wrap">
                  {isEditing ? (
                    <>
                      <button
                        onClick={() => saveEdit(s)}
                        disabled={saving}
                        className="px-2.5 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
                        data-testid={`save-edit-${s.gohimalayan_id}`}
                      >
                        {saving ? '…' : <><Save size={12} /> Save</>}
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={saving}
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-600"
                        title="Cancel"
                        data-testid={`cancel-edit-${s.gohimalayan_id}`}
                      ><X size={13} /></button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => startEdit(s)}
                        className="p-1.5 rounded hover:bg-amber-100 text-amber-700"
                        title={adminPwd ? 'Edit host & WhatsApp' : 'Unlock to edit'}
                        data-testid={`edit-row-${s.gohimalayan_id}`}
                      ><Pencil size={13} /></button>
                      <button
                        onClick={() => copy(publicUrl, `link-${s.gohimalayan_id}`)}
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-600 hover:text-stone-900"
                        title="Copy public URL"
                        data-testid={`copy-link-${s.gohimalayan_id}`}
                      >
                        {copied === `link-${s.gohimalayan_id}` ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                      </button>
                      <a
                        href={s.public_path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded hover:bg-stone-100 text-stone-600 hover:text-stone-900"
                        title="Open page"
                        data-testid={`open-link-${s.gohimalayan_id}`}
                      ><ExternalLink size={13} /></a>
                      {hostWaUrl && (
                        <a
                          href={hostWaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
                          title="Message host"
                          data-testid={`msg-host-${s.gohimalayan_id}`}
                        ><MessageCircle size={13} /></a>
                      )}
                      {/* Discovered-only outreach quick actions */}
                      {effectiveTier(s) === 'discovered' && (
                        <>
                          {s.ig_handle && (
                            <a
                              href={`https://instagram.com/${s.ig_handle}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 rounded hover:bg-pink-100 text-pink-600"
                              title={`Open @${s.ig_handle} on Instagram`}
                              data-testid={`open-ig-${s.gohimalayan_id}`}
                            ><Instagram size={13} /></a>
                          )}
                          {s.outreach?.status && ['dm_sent', 'replied', 'in_conversation', 'applied', 'onboarded'].includes(s.outreach.status) ? (
                            <span
                              className="p-1.5 rounded bg-sky-100 text-sky-800 inline-flex items-center gap-1 text-[10px] font-medium"
                              title={`Outreach status: ${s.outreach.status}`}
                              data-testid={`outreach-status-${s.gohimalayan_id}`}
                            ><CheckCircle2 size={11} /> {s.outreach.status.replace(/_/g, ' ')}</span>
                          ) : (
                            <button
                              onClick={() => markContacted(s)}
                              disabled={contactingId === s.gohimalayan_id || !s.ig_handle}
                              className="p-1.5 rounded hover:bg-sky-100 text-sky-700 disabled:opacity-40 disabled:cursor-not-allowed"
                              title={s.ig_handle ? (adminPwd ? 'Mark as contacted (DM sent)' : 'Unlock to mark contacted') : 'No Instagram handle on this stay'}
                              data-testid={`mark-contacted-${s.gohimalayan_id}`}
                            >{contactingId === s.gohimalayan_id ? '…' : <Send size={13} />}</button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] text-stone-400 mt-4 leading-relaxed text-center">
          Live directory of every active GoHimalayan stay — new properties appear here automatically the moment they're approved in <Link to="/admin/review" className="underline">/admin/review</Link>.
          {!adminPwd && <> · Tap <b>Unlock to edit</b> to capture host name and WhatsApp numbers inline.</>}
        </p>
      </main>
    </div>
  );
}
